/* Centred dialog.

   Modals keep `transform-origin: center`. Popovers and menus scale from their
   trigger because they are anchored to it; a modal is anchored to nothing, so
   scaling it from a corner reads as arriving from somewhere it did not come
   from. The entrance starts at 0.97, never 0 — nothing in the physical world
   appears out of nothing, and scale(0) always looks like a bug.

   Enter and exit are asymmetric: the entrance can afford 240ms because the
   user is arriving at something new, while the exit takes 120ms because they
   have already decided and the system is only getting out of the way. */

import { useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { EASE_OUT } from '../../design/motion'
import { useScrollLock } from './useScrollLock'
import { useFocusTrap } from './useFocusTrap'

interface ModalProps {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  footer?: ReactNode
  /** Suppress the close button and outside-click for a dialog that demands an
   *  explicit answer. Escape still works — trapping a user is never correct. */
  insistent?: boolean
  className?: string
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  insistent = false,
  className,
}: ModalProps) {
  const { t } = useTranslation()
  const reduce = useReducedMotion()
  const panelRef = useRef<HTMLDivElement>(null)
  const titleId = useId()

  useScrollLock(open)
  useFocusTrap(panelRef, open, onClose)

  const node = (
    <AnimatePresence>
      {open && (
        <div className="modal-root">
          <motion.div
            className="modal-scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.16, ease: EASE_OUT }}
            onClick={insistent ? undefined : onClose}
          />
          <motion.div
            ref={panelRef}
            className={`modal-panel ${className ?? ''}`}
            role="dialog"
            aria-modal="true"
            {...(title ? { 'aria-labelledby': titleId } : {})}
            initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.97, y: 8 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.98, y: 4 }}
            transition={{ duration: reduce ? 0.12 : 0.24, ease: EASE_OUT }}
          >
            {(title || !insistent) && (
              <header className="modal-head">
                {title && (
                  <h2 id={titleId} className="modal-title">
                    {title}
                  </h2>
                )}
                {!insistent && (
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={onClose}
                    aria-label={t('common.close')}
                  >
                    <X size={19} />
                  </button>
                )}
              </header>
            )}
            <div className="modal-body">{children}</div>
            {footer && <footer className="modal-foot">{footer}</footer>}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )

  if (typeof document === 'undefined') return null
  return createPortal(node, document.body)
}

interface ConfirmProps {
  open: boolean
  title: string
  body?: string
  confirmLabel: string
  cancelLabel?: string
  destructive?: boolean
  busy?: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Confirmation for genuinely destructive, irreversible actions only.
 *
 * Used on anything reversible it becomes noise — people learn to click through
 * dialogs, and then the one that mattered gets clicked through too. Removing a
 * basket line offers Undo instead of asking first; clearing a whole basket
 * asks, because there is nothing left to undo from.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  destructive,
  busy,
  onConfirm,
  onCancel,
}: ConfirmProps) {
  const { t } = useTranslation()
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      className="modal-confirm"
      footer={
        <>
          <button type="button" className="btn ghost full" onClick={onCancel} disabled={busy}>
            {cancelLabel ?? t('common.cancel')}
          </button>
          <button
            type="button"
            className={`btn full ${destructive ? 'danger' : 'pri'}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      {body && <p className="modal-text">{body}</p>}
    </Modal>
  )
}
